/**
 * Automation Block Component (Doc-View)
 *
 * Renders the automation block in normal document view (inside a note).
 */

import { I18n } from '@affine/i18n';
import {
  ArrowRightBigIcon,
  DatabaseTableViewIcon,
  FilterIcon,
  PlayFillIcon,
} from '@blocksuite/icons/lit';
import { BlockComponent } from '@blocksuite/std';
import { html } from 'lit';

import type { AutomationBlockModel } from '../schemas/index.js';
import { applyPageModeFilter } from '../utils/page-mode-filter.js';
import * as styles from './automation-block.css.js';
import { resolveAutomationName } from './automation-name.js';

export const AFFINE_AUTOMATION = 'affine-automation';

export class AutomationBlockComponent extends BlockComponent<AutomationBlockModel> {
  override connectedCallback() {
    super.connectedCallback();

    // E018-SPK021: Hide canvas automations in page mode
    if (!applyPageModeFilter(this)) return;
  }

  override renderBlock() {
    const model = this.model;
    const workflowName = (model.props as Record<string, unknown>)
      .workflowName as string | null;
    const title = resolveAutomationName(
      {
        title: model.props.title,
        titleMode: model.props.titleMode,
        workflowName,
      },
      I18n.t('com.streamlain.streamlain.common.new-automation')
    );
    const { sourceTableId, workflowId, outputMode } = model.props;
    const { filterCount, mappingCount } = model;

    return html`
      <div class=${styles.automationBlock}>
        <div class=${styles.header}>
          <span class=${styles.icon}>${PlayFillIcon()}</span>
          <span class=${styles.name}>${title}</span>
        </div>

        <div class=${styles.summary}>
          <div class=${styles.summaryRow}>
            <span class=${styles.rowIcon}>${DatabaseTableViewIcon()}</span>
            <span>
              ${sourceTableId
                ? `Source table (${filterCount} filters, ${mappingCount} mappings)`
                : 'No source configured'}
            </span>
          </div>
          <div class=${styles.summaryRow}>
            <span class=${styles.rowIcon}>${FilterIcon()}</span>
            <span>${workflowId ? 'Workflow configured' : 'No workflow'}</span>
          </div>
          <div class=${styles.summaryRow}>
            <span class=${styles.rowIcon}>${ArrowRightBigIcon()}</span>
            <span>${this._formatOutputMode(outputMode)}</span>
          </div>
        </div>
      </div>
    `;
  }

  private _formatOutputMode(mode: string): string {
    switch (mode) {
      case 'update_source':
        return 'Update source table';
      case 'create_table':
        return 'Create new table';
      case 'append':
        return 'Append to target';
      case 'update_target':
        return 'Update target table';
      default:
        return mode;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_AUTOMATION]: AutomationBlockComponent;
  }
}
